/**
 * Mock nutrition data for development.
 * Includes meals spread across the last 7 days and nutrition goals.
 * Macros add up correctly: calories = (protein * 4) + (carbs * 4) + (fat * 9) approximately.
 */
import { subDays } from 'date-fns';

const now = new Date();
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);

const makeMeal = (overrides) => ({
  id: overrides.id || `mock_meal_${Date.now().toString(36)}`,
  title: 'Meal',
  mealType: 'snack',
  calories: 0,
  macros: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
  ingredients: [],
  recipe: null,
  imageUrl: null,
  date: today.toISOString(),
  sharedAsPostId: null,
  createdAt: new Date().toISOString(),
  updatedAt: null,
  ...overrides,
});

/**
 * Nutrition goals for the mock current user.
 */
export const MOCK_NUTRITION_GOALS = {
  dailyCalories: 2200,
  macroTargets: {
    protein: 160,
    carbs: 250,
    fat: 70,
    fiber: 30,
  },
  updatedAt: subDays(now, 14).toISOString(),
};

/**
 * Mock meals spread across the last 7 days.
 */
export const MOCK_MEALS = [
  // ── TODAY ─────────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_01',
    title: 'Overnight Oats with Berries',
    mealType: 'breakfast',
    calories: 420,
    macros: { protein: 24, carbs: 58, fat: 12, fiber: 7 },
    ingredients: ['Rolled oats (1/2 cup)', 'Whey protein (1 scoop)', 'Mixed berries (1/2 cup)', 'Almond milk (1 cup)', 'Chia seeds (1 tbsp)'],
    date: today.toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_02',
    title: 'Grilled Chicken Caesar Wrap',
    mealType: 'lunch',
    calories: 560,
    macros: { protein: 42, carbs: 48, fat: 22, fiber: 4 },
    ingredients: ['Whole wheat wrap', 'Grilled chicken breast (5oz)', 'Romaine lettuce', 'Parmesan (1oz)', 'Caesar dressing (1 tbsp)', 'Cherry tomatoes'],
    date: today.toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_03',
    title: 'Protein Recovery Shake',
    mealType: 'snack',
    calories: 340,
    macros: { protein: 40, carbs: 32, fat: 8, fiber: 3 },
    ingredients: ['Whey protein (2 scoops)', 'Banana', 'Almond milk (1 cup)', 'Ice'],
    date: today.toISOString(),
  }),

  // ── YESTERDAY ─────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_04',
    title: 'Scrambled Eggs & Toast',
    mealType: 'breakfast',
    calories: 480,
    macros: { protein: 30, carbs: 42, fat: 22, fiber: 3 },
    ingredients: ['Eggs (3 whole)', 'Whole wheat toast (2 slices)', 'Butter (1 tsp)', 'Spinach', 'Salt & pepper'],
    date: subDays(today, 1).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_05',
    title: 'Turkey & Avocado Sandwich',
    mealType: 'lunch',
    calories: 520,
    macros: { protein: 36, carbs: 44, fat: 24, fiber: 6 },
    ingredients: ['Sourdough bread (2 slices)', 'Turkey breast (4oz)', 'Avocado (1/2)', 'Tomato', 'Lettuce', 'Mustard'],
    date: subDays(today, 1).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_06',
    title: 'Salmon with Brown Rice',
    mealType: 'dinner',
    calories: 620,
    macros: { protein: 44, carbs: 55, fat: 24, fiber: 5 },
    ingredients: ['Salmon fillet (6oz)', 'Brown rice (1 cup cooked)', 'Steamed broccoli', 'Soy sauce', 'Ginger'],
    date: subDays(today, 1).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_07',
    title: 'Greek Yogurt & Nuts',
    mealType: 'snack',
    calories: 280,
    macros: { protein: 20, carbs: 22, fat: 14, fiber: 2 },
    ingredients: ['Greek yogurt (1 cup)', 'Mixed nuts (1oz)', 'Honey drizzle'],
    date: subDays(today, 1).toISOString(),
  }),

  // ── 2 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_08',
    title: 'Protein Pancakes',
    mealType: 'breakfast',
    calories: 440,
    macros: { protein: 32, carbs: 52, fat: 12, fiber: 4 },
    ingredients: ['Oat flour (1/2 cup)', 'Protein powder (1 scoop)', 'Egg whites (3)', 'Banana (1/2)', 'Maple syrup (1 tbsp)'],
    date: subDays(today, 2).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_09',
    title: 'Chicken Burrito Bowl',
    mealType: 'lunch',
    calories: 610,
    macros: { protein: 45, carbs: 62, fat: 20, fiber: 8 },
    ingredients: ['Chicken breast (5oz)', 'Brown rice (3/4 cup)', 'Black beans (1/4 cup)', 'Salsa', 'Corn', 'Lime'],
    date: subDays(today, 2).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_10',
    title: 'Steak & Sweet Potato',
    mealType: 'dinner',
    calories: 680,
    macros: { protein: 48, carbs: 52, fat: 30, fiber: 6 },
    ingredients: ['Sirloin steak (6oz)', 'Sweet potato (medium)', 'Green beans', 'Garlic butter (1 tsp)'],
    date: subDays(today, 2).toISOString(),
  }),

  // ── 3 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_11',
    title: 'Greek Yogurt Parfait',
    mealType: 'breakfast',
    calories: 380,
    macros: { protein: 28, carbs: 45, fat: 10, fiber: 4 },
    ingredients: ['Greek yogurt (1 cup)', 'Granola (1/4 cup)', 'Mixed berries', 'Honey'],
    date: subDays(today, 3).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_12',
    title: 'Tuna Poke Bowl',
    mealType: 'lunch',
    calories: 550,
    macros: { protein: 40, carbs: 58, fat: 18, fiber: 5 },
    ingredients: ['Ahi tuna (5oz)', 'Sushi rice (3/4 cup)', 'Edamame', 'Cucumber', 'Soy sauce', 'Sesame oil'],
    date: subDays(today, 3).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_13',
    title: 'Chicken Stir Fry',
    mealType: 'dinner',
    calories: 580,
    macros: { protein: 42, carbs: 52, fat: 22, fiber: 6 },
    ingredients: ['Chicken breast (5oz)', 'Mixed vegetables', 'White rice (3/4 cup)', 'Teriyaki sauce', 'Sesame seeds'],
    date: subDays(today, 3).toISOString(),
  }),

  // ── 4 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_14',
    title: 'Avocado Toast with Eggs',
    mealType: 'breakfast',
    calories: 460,
    macros: { protein: 22, carbs: 38, fat: 28, fiber: 8 },
    ingredients: ['Sourdough toast (2 slices)', 'Avocado (1/2)', 'Eggs (2)', 'Red pepper flakes', 'Lime'],
    date: subDays(today, 4).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_15',
    title: 'Ground Turkey Lettuce Wraps',
    mealType: 'lunch',
    calories: 420,
    macros: { protein: 38, carbs: 22, fat: 22, fiber: 4 },
    ingredients: ['Ground turkey (5oz)', 'Butter lettuce leaves', 'Water chestnuts', 'Hoisin sauce', 'Green onions'],
    date: subDays(today, 4).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_16',
    title: 'Pasta Primavera',
    mealType: 'dinner',
    calories: 580,
    macros: { protein: 28, carbs: 72, fat: 20, fiber: 6 },
    ingredients: ['Whole wheat pasta (2oz dry)', 'Zucchini', 'Bell peppers', 'Cherry tomatoes', 'Olive oil', 'Parmesan', 'Chicken breast (4oz)'],
    date: subDays(today, 4).toISOString(),
  }),

  // ── 5 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_17',
    title: 'Smoothie Bowl',
    mealType: 'breakfast',
    calories: 400,
    macros: { protein: 26, carbs: 55, fat: 10, fiber: 8 },
    ingredients: ['Frozen acai pack', 'Banana (1/2)', 'Protein powder (1 scoop)', 'Almond milk', 'Granola topping', 'Coconut flakes'],
    date: subDays(today, 5).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_18',
    title: 'Grilled Chicken Salad',
    mealType: 'lunch',
    calories: 450,
    macros: { protein: 40, carbs: 28, fat: 22, fiber: 6 },
    ingredients: ['Grilled chicken (5oz)', 'Mixed greens', 'Cucumber', 'Feta cheese (1oz)', 'Olive oil vinaigrette', 'Walnuts'],
    date: subDays(today, 5).toISOString(),
  }),

  // ── 6 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_19',
    title: 'Egg White Omelette',
    mealType: 'breakfast',
    calories: 320,
    macros: { protein: 32, carbs: 18, fat: 14, fiber: 3 },
    ingredients: ['Egg whites (5)', 'Spinach', 'Mushrooms', 'Feta cheese (1oz)', 'Whole wheat toast (1 slice)'],
    date: subDays(today, 6).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_20',
    title: 'BBQ Chicken & Rice',
    mealType: 'dinner',
    calories: 640,
    macros: { protein: 46, carbs: 68, fat: 18, fiber: 4 },
    ingredients: ['Chicken thigh (6oz)', 'White rice (1 cup cooked)', 'BBQ sauce (2 tbsp)', 'Coleslaw'],
    date: subDays(today, 6).toISOString(),
  }),

  // ── 7 DAYS AGO ────────────────────────────────────────────────────────────
  makeMeal({
    id: 'mock_meal_21',
    title: 'Oatmeal with Peanut Butter',
    mealType: 'breakfast',
    calories: 450,
    macros: { protein: 20, carbs: 58, fat: 16, fiber: 6 },
    ingredients: ['Oats (1/2 cup)', 'Peanut butter (1 tbsp)', 'Banana', 'Honey', 'Cinnamon'],
    date: subDays(today, 7).toISOString(),
  }),
  makeMeal({
    id: 'mock_meal_22',
    title: 'Shrimp Tacos',
    mealType: 'dinner',
    calories: 520,
    macros: { protein: 34, carbs: 48, fat: 22, fiber: 5 },
    ingredients: ['Shrimp (5oz)', 'Corn tortillas (3)', 'Cabbage slaw', 'Lime crema', 'Cilantro', 'Lime'],
    date: subDays(today, 7).toISOString(),
  }),
];
